import { Box } from '@mui/material';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { Attachment } from 'services/chat';

// Constants
export const CDN_BASE_URL = process.env.REACT_APP_CDN_URL || 'https://cdn.telagri.dev/';

// Utility functions
export const getFullUrl = (path: string): string => {
	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path;
	}
	return `${CDN_BASE_URL}${path.startsWith('/') ? path.substring(1) : path}`;
};

export const isVoiceMessage = (attachment: Attachment): boolean => {
	// First check if it has voice message indicators in filename/s3_key
	const hasVoiceMessagePattern = !!(
		attachment.fileName?.startsWith('voice-message-') || 
		attachment.s3_key?.includes('voice-message-')
	);
	
	// If it has voice message patterns, check if it's an audio file OR if backend incorrectly marked it as video
	if (hasVoiceMessagePattern) {
		return !!(
			attachment.fileType?.startsWith('audio/') ||
			attachment.fileType === 'video' || // Backend bug: voice messages marked as video
			attachment.fileName?.endsWith('.wav') ||
			attachment.fileName?.endsWith('.mp3') ||
			attachment.fileName?.endsWith('.m4a')
		);
	}
	
	// For non-voice-message patterns, only accept actual audio files
	return !!(attachment.fileType?.startsWith('audio/'));
};

// Rich text content component with improved processing
export const RichTextContent = React.memo<{ html: string }>(({ html }) => {
	const processedHtml = useMemo(() => {
		// Quick early returns for common cases
		if (!html || html === '<p><br></p>' || html === '<p></p>') {
			return '';
		}

		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;

		// Remove empty list items more efficiently
		const listItems = tempDiv.querySelectorAll('li');
		listItems.forEach((li) => {
			const textContent = li.textContent?.trim();
			const hasOnlyBr =
				(li.childNodes.length === 1 && li.childNodes[0].nodeName === 'BR') ||
				li.innerHTML === '<br>' ||
				li.innerHTML === '<br/>';

			if (!textContent || hasOnlyBr) {
				li.style.display = 'none';
			}
		});

		// Remove empty paragraphs and lists
		const emptyP = tempDiv.querySelectorAll('p:empty, p:has(br:only-child)');
		emptyP.forEach((p) => p.remove());

		const emptyLists = tempDiv.querySelectorAll('ul:empty, ol:empty');
		emptyLists.forEach((list) => list.remove());

		const sanitizedHtml = DOMPurify.sanitize(tempDiv.innerHTML, {
			ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'span'],
			ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
		});

		// Ensure all links open in new tab for security
		const finalDiv = document.createElement('div');
		finalDiv.innerHTML = sanitizedHtml;
		const links = finalDiv.querySelectorAll('a');
		links.forEach((link) => {
			link.setAttribute('target', '_blank');
			link.setAttribute('rel', 'noopener noreferrer');
		});

		return finalDiv.innerHTML;
	}, [html]);

	if (!processedHtml) {
		return null;
	}

	return (
		<Box
			sx={{
				wordBreak: 'break-word',
				lineHeight: 1.4,
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
				'& p': {
					margin: 0,
					'&:not(:first-of-type)': { marginTop: '0.5em' },
				},
				'& a': {
					color: '#0078d4',
					textDecoration: 'none',
					'&:hover': { textDecoration: 'underline' },
				},
				'& ul, & ol': {
					paddingLeft: '20px',
					margin: '0.5em 0',
				},
				'& li': {
					margin: '0.25em 0',
					paddingLeft: '4px',
				},
				'& ul': { listStyleType: 'disc' },
				'& ol': { listStyleType: 'decimal' },
				'& ul ul': { listStyleType: 'circle' },
				'& ul ul ul': { listStyleType: 'square' },
				'& ol ol': { listStyleType: 'lower-alpha' },
				'& ol ol ol': { listStyleType: 'lower-roman' },
			}}
			dangerouslySetInnerHTML={{ __html: processedHtml }}
		/>
	);
});

// Timestamp formatting utility
export const formatTimestamp = (createDate: number | string): string => {
	// Handle both seconds (Unix timestamp) and milliseconds (from Date.getTime())
	let timestampMs: number;
	if (typeof createDate === 'number') {
		// If it's less than a reasonable millisecond timestamp, assume it's seconds
		if (createDate < 1000000000000) {
			timestampMs = createDate * 1000; // Convert seconds to milliseconds
		} else {
			timestampMs = createDate; // Already in milliseconds
		}
	} else {
		timestampMs = Number(createDate);
	}
	
	const messageDate = new Date(timestampMs);
	const now = new Date();
	const isToday = messageDate.toDateString() === now.toDateString();

	if (isToday) {
		return messageDate.toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
		});
	}

	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	const isYesterday = messageDate.toDateString() === yesterday.toDateString();

	if (isYesterday) {
		return 'Yesterday';
	}

	return messageDate.toLocaleDateString([], {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};
